import angular from 'angular';
import { AbstractCollectionDataProvider } from './abstract-collection-data-provider';
import { type ICollectionParams, type IResourceParams, type ISorting, type IPagination, type IItem, type IDataProvider, Events } from './models';
import { UNLIMITED_AMOUNT } from './abstract-data-provider-v2';

export interface ICustomDataProvider extends Omit<IDataProvider<IItem>, 'apiUrl'> {
    setData(data: any[]): void;
    getData(): any[];
    fillDataFromDataProvider(collectionParams: ICollectionParams, resourceParams?: IResourceParams, cancel?: Promise<any> | number): Promise<any>;
}

export class CustomDataProvider<Provider extends IDataProvider<IItem> = IDataProvider<IItem>>
    extends AbstractCollectionDataProvider<IItem>
    implements ICustomDataProvider
{
    protected dataProvider: Provider;
    protected page: IPagination;
    protected specialResourceParams: IResourceParams = {};
    protected onUpdate: () => Promise<void> = () => Promise.resolve();

    public setData(data: any[]) {
        this.data = data;
    }

    public getData() {
        return this.data;
    }

    public setDataProvider(dataProvider: Provider) {
        this.dataProvider = dataProvider;

        this.dataProvider.on(Events.Updated, params => {
            this.onUpdate().then(() => {
                this.emit(Events.Updated, params);
            });
        });

        this.dataProvider.on(Events.Removed, params => {
            this.onUpdate().then(() => {
                this.emit(Events.Removed, params);
            });
        });
    }

    public list(collectionParams: ICollectionParams, _resourceParam: IResourceParams, _timeout: Promise<void> = null) {
        return new Promise<any>(resolve => {
            resolve({
                items: this.sort(angular.copy(this.data), collectionParams.sorting),
                page: this.page
            });
        });
    }

    public all(collectionParams: ICollectionParams = {}, _resourceParam: IResourceParams = {}, _timeout: Promise<void> = null) {
        collectionParams.pagination = { size: UNLIMITED_AMOUNT, number: 0 };
        return this.list(collectionParams, _resourceParam, _timeout).then(list => list.items);
    }

    public fillDataFromDataProvider(collectionParams: ICollectionParams = {}, resourceParam: IResourceParams = {}, cancel: Promise<void> = null) {
        this.specialResourceParams = resourceParam;
        return new Promise<any>((resolve, reject) => {
            this.dataProvider
                .list(collectionParams, resourceParam, cancel)
                .then(list => {
                    this.data = list.items;
                    this.page = list.page;
                    resolve(angular.copy(list.items));
                })
                .catch(() => {
                    reject();
                });
        });
    }

    protected sort(data, sorting: ISorting[] = []) {
        if (!sorting || !sorting.length) {
            return data;
        }

        sorting.sort((a, b) => a.priority - b.priority);

        return data.sort((a, b) => {
            let result = 0;
            for (let i = 0; i < sorting.length && result === 0; i++) {
                const sort = sorting[i];
                switch (sort.direction) {
                    case 'asc':
                        result = this.ascSort(a, b, sort.field);
                        break;
                    case 'desc':
                        result = this.descSort(a, b, sort.field);
                        break;
                }
            }
            return result;
        });
    }

    private ascSort(a, b, name) {
        const result = this.preSort(a, b, name);
        if (result !== undefined) {
            return result;
        }
        let value1 = +a[name];
        let value2 = +b[name];
        if (value1 !== value1) {
            value1 = a[name];
            value2 = b[name];
        }
        return value1 > value2 ? 1 : value1 < value2 ? -1 : 0;
    }

    private descSort(a, b, name) {
        const result = this.preSort(a, b, name);
        if (result !== undefined) {
            return -1 * result;
        }

        let value1 = +a[name];
        let value2 = +b[name];
        if (value1 !== value1) {
            value1 = a[name];
            value2 = b[name];
        }
        return value1 < value2 ? 1 : value1 > value2 ? -1 : 0;
    }

    protected preSort(_a, _b, _name) {
        return undefined;
    }

    protected filterByPagination(data: any[], collectionParams: ICollectionParams) {
        if (!collectionParams.pagination) {
            return data;
        }
        const currentPage = collectionParams.pagination.number;
        const itemsOnPage = collectionParams.pagination.size;
        collectionParams.pagination.totalElements = data.length;
        collectionParams.pagination.totalPages = Math.ceil(data.length / itemsOnPage);
        return data.slice(currentPage * itemsOnPage, currentPage * itemsOnPage + itemsOnPage);
    }

    protected getResourceUrl(resourceParams: IResourceParams = {}) {
        return (
            (this.dataProvider &&
                this.dataProvider['getResourceUrl'] &&
                this.dataProvider['getResourceUrl']({
                    ...this.specialResourceParams,
                    ...resourceParams
                })) ||
            ''
        );
    }
}
